import { Box, Button, Dialog, Grid } from '@mui/material';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTransportTypes, fetchUserFacilities } from '../../../../redux/slices/transports';
import { useScopeCheck } from '../../../../reusable-components/scopes';
import { TRANSPORT_ADMIN, TRANSPORT_COORDINATOR, TRANSPORT_NURSE, TIME_GRID_DAY } from '../constants';
import NewRequestForm from '../SaveRequest/NewRequestForm';
import CalendarOptions from './CalendarOptions';
import CalendarViewToggle from './CalendarViewToggle';
import { DirectionFilter } from './DirectionFilter';
import { FacilityFilter } from './FacilityFilter';
import { StatusFilter } from './StatusFilter';

const FILTER_KEYS = {
  status: 'calendar_status_filter',
  facility: 'calendar_facility_filter',
  direction: 'calendar_direction_filter',
};

export default function CalendarView() {
  const [isAddingNewRequest, setIsAddingNewRequest] = useState(false);
  const [dialogSize, setDialogSize] = useState('sm');
  const [statusFilter, setStatusFilter] = useState([]);
  const [facilityFilter, setFacilityFilter] = useState([]);
  const [directionFilter, setDirectionFilter] = useState([]);
  const [calendarViewType, setCalendarViewType] = useState(TIME_GRID_DAY);
  const dispatch = useDispatch();
  const transportsState = useSelector((state) => state.transports);
  const facilities = transportsState?.data?.facilities ?? [];
  const transportTypes = transportsState?.data?.transportTypes ?? [];
  const isLoading = transportsState?.isLoading ?? false;
  const allowAddRequest = useScopeCheck([TRANSPORT_ADMIN, TRANSPORT_COORDINATOR, TRANSPORT_NURSE], false, true);

  // Restore filters from sessionStorage on mount
  useEffect(() => {
    const storedStatus = sessionStorage.getItem(FILTER_KEYS.status);
    const storedFacility = sessionStorage.getItem(FILTER_KEYS.facility);
    const storedDirection = sessionStorage.getItem(FILTER_KEYS.direction);
    if (storedStatus) {
      try {
        setStatusFilter(JSON.parse(storedStatus));
      } catch (e) {
        // Ignore JSON parse error for status filter
      }
    }
    if (storedFacility) {
      try {
        setFacilityFilter(JSON.parse(storedFacility));
      } catch (e) {
        // Ignore JSON parse error for facility filter
      }
    }
    if (storedDirection) {
      try {
        setDirectionFilter(JSON.parse(storedDirection));
      } catch (e) {
        // Ignore JSON parse error for direction filter
      }
    }
  }, []);

  useEffect(() => {
    if (!facilities.length) {
      dispatch(fetchUserFacilities());
    }
  }, [dispatch, facilities.length]);

  useEffect(() => {
    if (transportTypes.length === 0) {
      dispatch(fetchTransportTypes());
    }
  }, [dispatch, transportTypes.length]);

  const handleStatusFilterChange = (event, newFilter) => {
    setStatusFilter(newFilter);
    sessionStorage.setItem(FILTER_KEYS.status, JSON.stringify(newFilter));
  };

  const handleFacilityChange = (event, value) => {
    setFacilityFilter(value);
    sessionStorage.setItem(FILTER_KEYS.facility, JSON.stringify(value));
  };

  const handleDirectionFilterChange = (event, newFilter) => {
    setDirectionFilter(newFilter);
    sessionStorage.setItem(FILTER_KEYS.direction, JSON.stringify(newFilter));
  };

  const handleCalendarViewChange = (_, value) => {
    if (value) setCalendarViewType(value);
  };

  const selectedStatusValues = statusFilter.map((item) => item.value);

  const selectedTripDirectionValues = directionFilter.map((item) => item.value);

  const selectedFacilityValues = facilityFilter.map((item) => item.value);

  return (
    <div>
      <Box sx={{ flexDirection: 'column', gap: 2, mt: 2, mb: 5, width: '100%' }}>
        <Grid container alignItems="center" spacing={1} sx={{ width: '100%' }}>
          <Grid item>
            <CalendarViewToggle value={calendarViewType} onChange={handleCalendarViewChange} />
          </Grid>
          <Grid item>
            <FacilityFilter
              facilities={facilities}
              facilityFilter={facilityFilter}
              handleFacilityChange={handleFacilityChange}
              isLoading={isLoading}
            />
          </Grid>
          <Grid item>
            <StatusFilter statusFilter={statusFilter} handleStatusFilterChange={handleStatusFilterChange} />
          </Grid>
          <Grid item>
            <DirectionFilter directionFilter={directionFilter} handleDirectionFilterChange={handleDirectionFilterChange} />
          </Grid>
          {allowAddRequest && (
            <Grid item sx={{ ml: 'auto' }}>
              <Button
                onClick={() => setIsAddingNewRequest(true)}
                sx={{ padding: '3px 16px', height: 40, display: 'flex', alignItems: 'center' }}
                variant="contained"
                color="secondary"
              >
                New Transport Request
              </Button>
              <Dialog
                open={isAddingNewRequest}
                onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                  setIsAddingNewRequest(false);
                }}
                fullWidth
                maxWidth={dialogSize}
              >
                <NewRequestForm
                  setOpen={(open) => {
                    setIsAddingNewRequest(open);
                    setDialogSize('sm');
                  }}
                  facilities={facilities ?? []}
                  onDialogSizeChange={setDialogSize}
                />
              </Dialog>
            </Grid>
          )}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <CalendarOptions
              statusFilter={selectedStatusValues}
              tripDirection={selectedTripDirectionValues}
              facilityFilter={selectedFacilityValues}
              selectedViewType={calendarViewType}
              onViewTypeChange={setCalendarViewType}
            />
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
